import { useCallback, useEffect, useRef, useState } from 'react';
import { buildVocabulary, vectorize } from './textVectorizer';

export type TextTrainResult = { accuracy: number; labels: string[] };
export type TextClassifyResult = { label: string; confidence: number }[];
export type TextTrainingSample = { text: string; label: string };
export interface TextModel {
  status: 'idle' | 'loading' | 'training' | 'ready' | 'error';
  progress: number;
  accuracy: number | null;
  evalCount: number | null;
  isReady: boolean;
  train: (samples: TextTrainingSample[]) => Promise<TextTrainResult>;
  classify: (text: string) => Promise<TextClassifyResult>;
  reset: () => void;
}

type DiyClassifier = {
  addData: (xs: number[], ys: string[]) => void;
  normalizeData: () => void;
  train: (options: { epochs: number }, callback: (error?: Error) => void) => void;
  classify: (xs: number[]) => Promise<Array<{ label: string; confidence: number }>>;
};

export function useTextModel(): TextModel {
  const [status, setStatus] = useState<TextModel['status']>('idle');
  const [progress, setProgress] = useState(0);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [evalCount, setEvalCount] = useState<number | null>(null);
  const mounted = useRef(true);
  const classifierRef = useRef<DiyClassifier | null>(null);
  const vocabularyRef = useRef<string[]>([]);

  useEffect(() => () => { mounted.current = false; }, []);

  const train = useCallback(async (samples: TextTrainingSample[]): Promise<TextTrainResult> => {
    const usable = samples.filter((sample) => sample.label.trim() && sample.text.trim());
    const labels = Array.from(new Set(usable.map((sample) => sample.label)));
    if (labels.length < 2) throw new Error('Ajoutez au moins deux labels avant de lancer l’entraînement.');
    if (labels.some((label) => !usable.some((sample) => sample.label === label))) {
      throw new Error('Chaque label doit contenir au moins une phrase.');
    }
    if (mounted.current) {
      setStatus('loading');
      setProgress(0);
      setAccuracy(null);
    }
    try {
      // ml5 is intentionally lazy-loaded so TensorFlow is kept out of the main chunk.
      // @ts-ignore ml5@0.12.2 has no bundled TypeScript declarations.
      const ml5Module = await import('ml5');
      const ml5 = (ml5Module as any).default ?? ml5Module;
      const vocabulary = buildVocabulary(usable.map((sample) => sample.text));
      vocabularyRef.current = vocabulary;
      const classifier: DiyClassifier = ml5.neuralNetwork({ task: 'classification', debug: false });
      classifierRef.current = classifier;

      const byLabel = new Map<string, TextTrainingSample[]>();
      usable.forEach((sample) =>
        byLabel.set(sample.label, [...(byLabel.get(sample.label) || []), sample]),
      );
      const evaluation: TextTrainingSample[] = [];
      byLabel.forEach((group) => {
        if (group.length >= 2) evaluation.push(group[group.length - 1]);
      });
      const training = usable.filter((sample) => !evaluation.includes(sample));
      const trainSet = training.length ? training : usable;

      trainSet.forEach((sample) => {
        classifier.addData(vectorize(sample.text, vocabulary), [sample.label]);
      });

      if (mounted.current) setStatus('training');
      // DiyNeuralNetwork.train() is callback-only — it must be wrapped in a Promise.
      await new Promise<void>((resolve, reject) => {
        classifier.train({ epochs: 32 }, (error) => {
          if (error) reject(error);
          else resolve();
        });
      });
      if (mounted.current) setProgress(100);

      const evalSet = evaluation.length ? evaluation : trainSet;
      let correct = 0;
      for (const sample of evalSet) {
        const results = await classifier.classify(vectorize(sample.text, vocabulary));
        if (results[0]?.label === sample.label) correct += 1;
      }
      const result = Math.round((100 * correct) / evalSet.length);
      if (mounted.current) {
        setAccuracy(result);
        setEvalCount(evalSet.length);
        setStatus('ready');
      }
      return { accuracy: result, labels };
    } catch (error) {
      if (mounted.current) setStatus('error');
      throw error;
    }
  }, []);

  const classify = useCallback(async (text: string): Promise<TextClassifyResult> => {
    if (!classifierRef.current) throw new Error('Entraînez d’abord le modèle avant de tester une phrase.');
    const results = await classifierRef.current.classify(vectorize(text, vocabularyRef.current));
    return [...results].sort((a, b) => b.confidence - a.confidence);
  }, []);

  const reset = useCallback(() => {
    classifierRef.current = null;
    vocabularyRef.current = [];
    if (mounted.current) {
      setStatus('idle');
      setProgress(0);
      setAccuracy(null);
      setEvalCount(null);
    }
  }, []);

  return {
    status,
    progress,
    accuracy,
    evalCount,
    isReady: status === 'ready' && !!classifierRef.current,
    train,
    classify,
    reset,
  };
}
